<template>
  <section class="mtt-workout-customers">
    <div class="mtt-hero mb-5">
      <div class="mtt-hero-icon" aria-hidden="true">
        <v-icon color="primary" size="30">mdi-account-heart-outline</v-icon>
      </div>
      <div class="mtt-hero-copy">
        <h1 class="text-h4 font-weight-bold white--text mb-2">MTT-Kunden</h1>
        <p class="mtt-muted mb-0">Trainingspläne für berechtigte Kunden im aktuellen Studio erstellen und verwalten.</p>
      </div>
    </div>

    <v-row class="mtt-layout">
      <v-col cols="12" md="4">
        <v-card dark class="mtt-panel mtt-customer-panel rounded-xl" outlined>
          <v-card-title class="customer-panel-title pb-2">
            <span>Kunden finden</span>
            <span v-if="total" class="customer-count">{{ total }}</span>
          </v-card-title>
          <v-card-text>
            <v-text-field
              v-model="search"
              filled
              rounded
              dense
              clearable
              hide-details
              prepend-inner-icon="mdi-magnify"
              class="customer-search"
              label="Vor- oder Nachname"
              @input="queueCustomerSearch" />
          </v-card-text>

          <v-progress-linear v-if="customersLoading" indeterminate color="primary" />

          <v-list v-if="customers.length" dark color="transparent" class="py-0 customer-list">
            <v-list-item
              v-for="customer in customers"
              :key="customer._id"
              :class="{ 'customer-list-item--active': selectedCustomer && selectedCustomer._id === customer._id }"
              class="customer-list-item"
              @click="openCustomer(customer)">
              <v-list-item-avatar color="primary" size="44" class="customer-avatar">
                <v-img
                  v-if="customerPicture(customer)"
                  :src="customerPicture(customer)"
                  :alt="`Profilbild von ${customer.vorname || ''} ${customer.nachname || ''}`"
                  @error="markCustomerPictureFailed(customer)" />
                <span v-else class="white--text font-weight-bold">{{ initials(customer) }}</span>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title class="customer-name font-weight-medium">{{ customer.vorname }} {{ customer.nachname }}</v-list-item-title>
              </v-list-item-content>
              <v-list-item-icon><v-icon small color="grey lighten-1">mdi-chevron-right</v-icon></v-list-item-icon>
            </v-list-item>
          </v-list>

          <div v-else-if="!customersLoading" class="empty-list pa-8 text-center">
            <v-icon size="44" color="grey">mdi-account-search-outline</v-icon>
            <div class="font-weight-bold mt-3 white--text">Keine MTT-Kunden gefunden</div>
            <div class="caption grey--text mt-1">Die Suche berücksichtigt nur aktuell berechtigte MTT-Verträge.</div>
          </div>

          <v-card-actions v-if="pages > 1" class="justify-center px-2 pb-4">
            <v-pagination v-model="page" :length="pages" :total-visible="5" color="primary" @input="loadCustomers" />
          </v-card-actions>
        </v-card>
      </v-col>

      <v-col cols="12" md="8">
        <v-card v-if="!selectedCustomer" dark class="mtt-panel empty-selection rounded-xl" outlined>
          <v-icon size="72" color="primary">mdi-clipboard-account-outline</v-icon>
          <h2 class="text-h5 font-weight-bold mt-5 mb-2">Kunde auswählen</h2>
          <p class="mtt-muted mb-0">Wähle links einen MTT-Kunden, um die Trainingspläne zu verwalten.</p>
        </v-card>

        <v-card v-else dark class="mtt-panel rounded-xl" outlined>
          <div class="customer-header pa-5 pa-sm-6">
            <div class="customer-identity">
              <v-avatar color="primary" size="58" class="customer-avatar customer-avatar--large">
                <v-img
                  v-if="customerPicture(selectedCustomer)"
                  :src="customerPicture(selectedCustomer)"
                  :alt="`Profilbild von ${selectedCustomer.vorname || ''} ${selectedCustomer.nachname || ''}`"
                  @error="markCustomerPictureFailed(selectedCustomer)" />
                <span v-else class="white--text text-subtitle-1 font-weight-bold">{{ initials(selectedCustomer) }}</span>
              </v-avatar>
              <div>
                <div class="customer-kicker">Trainingspläne für</div>
                <h2 class="text-h5 font-weight-bold white--text mb-1">{{ selectedCustomer.vorname }} {{ selectedCustomer.nachname }}</h2>
                <div class="caption mtt-muted">Aktive und archivierte Pläne verwalten</div>
              </div>
            </div>
            <v-btn
              color="primary"
              depressed
              rounded
              class="new-plan-button white--text mt-4 mt-sm-0"
              :disabled="plansLoading"
              @click="requestCreatePlan">
              <v-icon left>mdi-plus</v-icon>
              Neuer Plan
            </v-btn>
          </div>

          <v-divider />
          <v-progress-linear v-if="plansLoading" indeterminate color="primary" />

          <div v-if="!plansLoading" class="pa-4 pa-sm-6">
            <section>
              <div class="section-heading mb-3">
                <div>
                  <h3 class="text-subtitle-1 font-weight-bold white--text">Aktive Pläne</h3>
                  <div class="caption grey--text">Der neueste Plan wird beim Kunden angezeigt.</div>
                </div>
                <v-chip small outlined>{{ activePlans.length }}</v-chip>
              </div>

              <div v-if="activePlans.length">
                <v-card v-for="plan in activePlans" :key="plan._id" class="plan-card mb-3" outlined dark>
                  <div class="plan-card-content pa-4">
                    <div class="plan-main">
                      <div class="d-flex align-center flex-wrap mb-1">
                        <span class="text-subtitle-1 font-weight-bold mr-2">{{ planTitle(plan) }}</span>
                        <v-chip v-if="plan._id === currentPlanId" x-small color="primary" class="current-plan-chip white--text font-weight-bold">
                          Aktuell beim Kunden
                        </v-chip>
                      </div>
                      <div class="caption grey--text">
                        {{ splitCount(plan) }} {{ splitCount(plan) === 1 ? "Split" : "Splits" }} · Erstellt {{ formatDate(plan.createdAt) }}
                      </div>
                    </div>
                    <div class="plan-actions mt-3 mt-sm-0">
                      <v-btn small text color="primary" @click="editPlan(plan)"><v-icon left small>mdi-pencil</v-icon>Bearbeiten</v-btn>
                      <v-menu offset-y bottom left>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn small text color="grey lighten-2" v-bind="attrs" v-on="on" :disabled="splitCount(plan) === 0">
                            <v-icon left small>mdi-file-pdf-box</v-icon>PDF
                          </v-btn>
                        </template>
                        <v-list dense dark>
                          <v-list-item v-for="(split, index) in planSplits(plan)" :key="`${plan._id}-${index}`" @click="downloadSplit(plan, index)">
                            <v-list-item-icon><v-icon small>mdi-file-pdf-box</v-icon></v-list-item-icon>
                            <v-list-item-title>{{ split.title || `Split ${index + 1}` }}</v-list-item-title>
                          </v-list-item>
                        </v-list>
                      </v-menu>
                      <v-btn small text color="orange lighten-1" @click="requestArchive(plan)">
                        <v-icon left small>mdi-archive-arrow-down-outline</v-icon>Archivieren
                      </v-btn>
                    </div>
                  </div>
                </v-card>
              </div>
              <div v-else class="plan-empty pa-6 text-center rounded-lg">
                <v-icon color="grey" size="38">mdi-clipboard-plus-outline</v-icon>
                <div class="mt-2 mtt-muted">Noch kein aktiver Trainingsplan.</div>
              </div>
            </section>

            <section v-if="archivedPlans.length" class="mt-7">
              <div class="section-heading mb-3">
                <div>
                  <h3 class="text-subtitle-1 font-weight-bold white--text">Archiv</h3>
                  <div class="caption grey--text">Archivierte Pläne können gedruckt oder wiederhergestellt werden.</div>
                </div>
                <v-chip small outlined>{{ archivedPlans.length }}</v-chip>
              </div>

              <v-card v-for="plan in archivedPlans" :key="plan._id" class="plan-card plan-card--archived mb-3" outlined dark>
                <div class="plan-card-content pa-4">
                  <div class="plan-main">
                    <div class="text-subtitle-1 font-weight-bold">{{ planTitle(plan) }}</div>
                    <div class="caption grey--text">{{ splitCount(plan) }} {{ splitCount(plan) === 1 ? "Split" : "Splits" }} · Archiviert</div>
                  </div>
                  <div class="plan-actions mt-3 mt-sm-0">
                    <v-menu offset-y bottom left>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn small text color="grey lighten-2" v-bind="attrs" v-on="on" :disabled="splitCount(plan) === 0">
                          <v-icon left small>mdi-file-pdf-box</v-icon>PDF
                        </v-btn>
                      </template>
                      <v-list dense dark>
                        <v-list-item
                          v-for="(split, index) in planSplits(plan)"
                          :key="`${plan._id}-archived-${index}`"
                          @click="downloadSplit(plan, index)">
                          <v-list-item-icon><v-icon small>mdi-file-pdf-box</v-icon></v-list-item-icon>
                          <v-list-item-title>{{ split.title || `Split ${index + 1}` }}</v-list-item-title>
                        </v-list-item>
                      </v-list>
                    </v-menu>
                    <v-btn small text color="green lighten-1" :loading="actionPlanId === plan._id" @click="restorePlan(plan)">
                      <v-icon left small>mdi-archive-arrow-up-outline</v-icon>Wiederherstellen
                    </v-btn>
                  </div>
                </div>
              </v-card>
            </section>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog v-model="showCreateWarning" max-width="500" dark>
      <v-card dark color="#26282c" class="mtt-dialog-card rounded-xl">
        <v-card-title class="font-weight-bold">Weiteren Plan erstellen?</v-card-title>
        <v-card-text>
          Nach dem Speichern wird der neueste Plan bei {{ selectedCustomerName }} angezeigt. Bestehende aktive Pläne werden nicht automatisch
          archiviert.
        </v-card-text>
        <v-card-actions class="pa-4 pt-0">
          <v-spacer />
          <v-btn text color="grey lighten-2" @click="showCreateWarning = false">Abbrechen</v-btn>
          <v-btn color="primary" class="white--text" depressed @click="createPlan">Plan erstellen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showArchiveDialog" max-width="460" dark>
      <v-card dark color="#26282c" class="mtt-dialog-card rounded-xl">
        <v-card-title class="font-weight-bold">Plan archivieren?</v-card-title>
        <v-card-text
          >Der Plan wird beim Kunden nicht mehr als aktiver Plan verwendet. Er bleibt im Archiv erhalten und kann wiederhergestellt
          werden.</v-card-text
        >
        <v-card-actions class="pa-4 pt-0">
          <v-spacer />
          <v-btn text color="grey lighten-2" @click="showArchiveDialog = false">Abbrechen</v-btn>
          <v-btn color="orange darken-2" class="white--text" depressed :loading="actionPlanId === archiveCandidateId" @click="archivePlan">
            Archivieren
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </section>
</template>

<script>
import api from "@/api"

export default {
  name: "MttWorkoutCustomers",
  data() {
    return {
      search: "",
      searchTimer: null,
      customers: [],
      customersLoading: false,
      customerRequestId: 0,
      failedCustomerPictures: {},
      plansLoading: false,
      plansRequestId: 0,
      selectedCustomer: null,
      workouts: [],
      page: 1,
      pages: 0,
      total: 0,
      limit: 20,
      actionPlanId: null,
      archiveCandidate: null,
      showArchiveDialog: false,
      showCreateWarning: false,
    }
  },
  computed: {
    activePlans() {
      return this.sortedPlans(this.workouts.filter((plan) => plan.deleted !== true))
    },
    archivedPlans() {
      return this.sortedPlans(this.workouts.filter((plan) => plan.deleted === true))
    },
    currentPlanId() {
      return this.activePlans.length ? this.activePlans[0]._id : null
    },
    selectedCustomerName() {
      if (!this.selectedCustomer) return "diesem Kunden"
      return `${this.selectedCustomer.vorname || ""} ${this.selectedCustomer.nachname || ""}`.trim()
    },
    archiveCandidateId() {
      return this.archiveCandidate ? this.archiveCandidate._id : null
    },
  },
  async created() {
    await this.loadCustomers()
    const customerId = this.$route.query.customerId
    if (customerId) await this.openCustomer(customerId, false)
  },
  beforeDestroy() {
    if (this.searchTimer) clearTimeout(this.searchTimer)
  },
  methods: {
    customerPicture(customer) {
      if (!customer || !customer._id) return null

      const failures = this.failedCustomerPictures[String(customer._id)] || {}
      const picture = typeof customer.bild === "string" ? customer.bild.trim() : ""

      if (picture && !failures.bild) {
        if (/^(data:image|https?:\/\/|blob:)/i.test(picture)) return picture
        if (picture.length > 200) return `data:image/jpeg;base64,${picture}`

        const normalizedPicture = picture.replace(/^\/+/, "")
        if (normalizedPicture.startsWith("img/")) return `/${normalizedPicture}`
        if (normalizedPicture.startsWith("profilbilder/")) return `/img/${normalizedPicture}`
        return `/img/profilbilder/${normalizedPicture}`
      }

      const tacPicture = typeof customer.tac_image === "string" ? customer.tac_image.trim() : ""
      if (tacPicture && !failures.tac) {
        if (/^(data:image|https?:\/\/|blob:)/i.test(tacPicture)) return tacPicture
        if (tacPicture.length > 200) return `data:image/jpeg;base64,${tacPicture}`
      }

      if ((customer.hasTacImage || tacPicture) && !failures.tac) {
        return `https://app.wellcomefit.ch/api/checkin/pool/images/${encodeURIComponent(customer._id)}`
      }

      return null
    },
    markCustomerPictureFailed(customer) {
      if (!customer || !customer._id) return
      const key = String(customer._id)
      const failures = this.failedCustomerPictures[key] || {}
      const failedSource = customer.bild && !failures.bild ? "bild" : "tac"
      this.$set(this.failedCustomerPictures, key, { ...failures, [failedSource]: true })
    },
    sortedPlans(plans) {
      return [...plans].sort((a, b) => {
        const aDate = new Date(a.createdAt || a.updatedAt || 0).getTime()
        const bDate = new Date(b.createdAt || b.updatedAt || 0).getTime()
        if (aDate !== bDate) return bDate - aDate
        return String(b._id || "").localeCompare(String(a._id || ""))
      })
    },
    initials(customer) {
      return `${customer.vorname || ""} ${customer.nachname || ""}`
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join("")
    },
    queueCustomerSearch() {
      if (this.searchTimer) clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => {
        this.page = 1
        this.loadCustomers()
      }, 300)
    },
    async loadCustomers() {
      const requestId = ++this.customerRequestId
      this.customersLoading = true
      try {
        const result = await api.workoutApi.fetchMttCustomers({
          q: this.search || "",
          page: this.page,
          limit: this.limit,
        })
        if (requestId !== this.customerRequestId) return
        this.customers = result.customers || []
        this.page = result.page || 1
        this.pages = result.pages || 0
        this.total = result.total || 0
      } catch (error) {
        if (requestId !== this.customerRequestId) return
        console.error("MTT-Kunden konnten nicht geladen werden:", error)
        this.customers = []
        this.$toast.error("MTT-Kunden konnten nicht geladen werden.")
      } finally {
        if (requestId === this.customerRequestId) this.customersLoading = false
      }
    },
    async openCustomer(customerOrId, updateRoute = true) {
      const customerId = typeof customerOrId === "string" ? customerOrId : customerOrId && customerOrId._id
      if (!customerId) return

      const requestId = ++this.plansRequestId
      this.plansLoading = true
      try {
        const result = await api.workoutApi.fetchMttCustomerWorkouts(customerId)
        if (requestId !== this.plansRequestId) return
        this.selectedCustomer = result.customer
        this.workouts = result.workouts || []
        if (updateRoute) this.updateCustomerQuery(customerId)
      } catch (error) {
        if (requestId !== this.plansRequestId) return
        console.error("MTT-Workout-Pläne konnten nicht geladen werden:", error)
        this.selectedCustomer = null
        this.workouts = []
        this.$toast.error("Der Kunde ist für MTT-Trainingspläne nicht mehr berechtigt.")
        await this.loadCustomers()
      } finally {
        if (requestId === this.plansRequestId) this.plansLoading = false
      }
    },
    updateCustomerQuery(customerId) {
      if (String(this.$route.query.customerId || "") === String(customerId)) return
      this.$router
        .replace({
          name: "Workouts_v2",
          query: { ...this.$route.query, mode: "mtt", customerId },
        })
        .catch(() => {})
    },
    requestCreatePlan() {
      if (!this.selectedCustomer) return
      if (this.workouts.length) {
        this.showCreateWarning = true
      } else {
        this.createPlan()
      }
    },
    createPlan() {
      if (!this.selectedCustomer) return
      this.showCreateWarning = false
      this.$router.push({ name: "MttWorkoutCreate", params: { customerId: this.selectedCustomer._id } })
    },
    editPlan(plan) {
      if (!this.selectedCustomer || plan.deleted === true) return
      this.$router.push({
        name: "MttWorkoutEdit",
        params: { customerId: this.selectedCustomer._id, id: plan._id },
      })
    },
    planSplits(plan) {
      return plan && plan.splits && Array.isArray(plan.splits.workouts) ? plan.splits.workouts : []
    },
    splitCount(plan) {
      return this.planSplits(plan).length
    },
    planTitle(plan) {
      return plan.title || (plan.splits && plan.splits.text) || "Trainingsplan"
    },
    formatDate(value) {
      if (!value) return "–"
      return new Intl.DateTimeFormat("de-CH", { day: "2-digit", month: "2-digit", year: "numeric" }).format(new Date(value))
    },
    async downloadSplit(plan, index) {
      this.$toast.info("Der PDF-Download wird vorbereitet.")
      try {
        await api.downloadSplitPDF(plan._id, index, true)
      } catch (error) {
        this.handleActionError(error, "Das PDF konnte nicht erstellt werden.")
      }
    },
    requestArchive(plan) {
      this.archiveCandidate = plan
      this.showArchiveDialog = true
    },
    async archivePlan() {
      if (!this.archiveCandidate) return
      this.actionPlanId = this.archiveCandidate._id
      try {
        await api.workoutApi.deleteWorkoutV2(this.archiveCandidate._id, true)
        this.$toast.success("Trainingsplan archiviert.")
        this.showArchiveDialog = false
        this.archiveCandidate = null
        await this.reloadSelectedCustomer()
      } catch (error) {
        this.handleActionError(error, "Der Trainingsplan konnte nicht archiviert werden.")
      } finally {
        this.actionPlanId = null
      }
    },
    async restorePlan(plan) {
      this.actionPlanId = plan._id
      try {
        await api.workoutApi.restoreWorkoutV2(plan._id, true)
        this.$toast.success("Trainingsplan wiederhergestellt.")
        await this.reloadSelectedCustomer()
      } catch (error) {
        this.handleActionError(error, "Der Trainingsplan konnte nicht wiederhergestellt werden.")
      } finally {
        this.actionPlanId = null
      }
    },
    async reloadSelectedCustomer() {
      if (!this.selectedCustomer) return
      await this.openCustomer(this.selectedCustomer._id, false)
    },
    handleActionError(error, fallbackMessage) {
      console.error(fallbackMessage, error)
      if (error && error.response && error.response.status === 403) {
        this.$toast.error("Die MTT-Berechtigung dieses Kunden ist abgelaufen.")
        this.showArchiveDialog = false
        this.archiveCandidate = null
        this.selectedCustomer = null
        this.workouts = []
        this.loadCustomers()
        return
      }
      this.$toast.error(fallbackMessage)
    },
  },
}
</script>

<style lang="scss" scoped>
.mtt-workout-customers {
  --mtt-surface: #25272b;
  --mtt-surface-soft: #2d2f34;
  --mtt-border: rgba(255, 255, 255, 0.12);
  --mtt-text: #f7f8f8;
  --mtt-muted: rgba(237, 241, 239, 0.7);
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: 12px 4px 40px;
  color: var(--mtt-text);
}

.customer-header,
.section-heading,
.plan-card-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.mtt-hero {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 16px;
  padding: 14px 4px 10px;
}

.mtt-hero-icon {
  width: 56px;
  height: 56px;
  flex: 0 0 56px;
  display: grid;
  place-items: center;
  border: 1px solid rgba(0, 210, 112, 0.28);
  border-radius: 18px;
  background: linear-gradient(145deg, rgba(0, 190, 95, 0.16), rgba(0, 190, 95, 0.04));
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.22);
}

.mtt-hero-copy {
  min-width: 0;
}

.mtt-muted {
  color: var(--mtt-muted) !important;
}

.mtt-panel {
  overflow: hidden;
  color: var(--mtt-text) !important;
  background: linear-gradient(145deg, rgba(44, 46, 51, 0.98), rgba(32, 34, 38, 0.99)) !important;
  border-color: var(--mtt-border) !important;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.2) !important;
}

.customer-panel-title {
  min-height: 64px;
  color: var(--mtt-text) !important;
}

.customer-count {
  min-width: 28px;
  height: 28px;
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 9px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 999px;
  color: var(--mtt-muted);
  background: rgba(255, 255, 255, 0.05);
  font-size: 0.75rem;
}

.customer-search ::v-deep .v-input__slot {
  color: var(--mtt-text) !important;
  background: rgba(255, 255, 255, 0.075) !important;
  border: 1px solid rgba(255, 255, 255, 0.05);
  box-shadow: none !important;
}

.customer-search ::v-deep input,
.customer-search ::v-deep .v-label,
.customer-search ::v-deep .v-icon {
  color: rgba(255, 255, 255, 0.82) !important;
}

.customer-list {
  max-height: 58vh;
  overflow-y: auto;
}

.customer-list-item {
  min-height: 64px;
  margin: 3px 10px;
  border: 1px solid transparent;
  border-radius: 14px;
  color: var(--mtt-text) !important;
  transition: background 0.2s ease, border-color 0.2s ease, transform 0.2s ease;
}

.customer-list-item:hover {
  transform: translateX(2px);
  border-color: rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.065);
}

.customer-list-item--active {
  border-color: rgba(0, 210, 112, 0.25);
  background: rgba(0, 190, 95, 0.12);
}

.customer-list-item--active {
  box-shadow: inset 3px 0 0 var(--v-primary-base);
}

.customer-name {
  color: var(--mtt-text) !important;
}

.customer-avatar {
  overflow: hidden;
  border: 2px solid rgba(255, 255, 255, 0.14);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.24);
}

.customer-avatar--large {
  flex: 0 0 58px;
}

.customer-identity {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 14px;
}

.customer-kicker {
  margin-bottom: 2px;
  color: var(--mtt-muted);
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.customer-header {
  background: linear-gradient(110deg, rgba(0, 190, 95, 0.08), transparent 55%);
}

.new-plan-button {
  box-shadow: 0 10px 24px rgba(0, 190, 95, 0.2) !important;
}

.empty-selection {
  min-height: 340px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  text-align: center;
}

.plan-card {
  color: var(--mtt-text) !important;
  background: rgba(255, 255, 255, 0.045) !important;
  border-color: rgba(255, 255, 255, 0.12) !important;
  transition: border-color 0.2s ease, background 0.2s ease, transform 0.2s ease;
}

.plan-card:hover {
  transform: translateY(-1px);
  border-color: rgba(255, 255, 255, 0.2) !important;
  background: rgba(255, 255, 255, 0.06) !important;
}

.plan-card--archived {
  opacity: 0.84;
}

.plan-main {
  min-width: 0;
}

.plan-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.current-plan-chip {
  letter-spacing: 0.01em;
}

.plan-empty,
.empty-list {
  color: var(--mtt-text);
  background: rgba(255, 255, 255, 0.025);
  border-top: 1px solid rgba(255, 255, 255, 0.04);
}

.mtt-panel ::v-deep .v-divider {
  border-color: rgba(255, 255, 255, 0.09) !important;
}

.mtt-panel ::v-deep .v-chip:not(.current-plan-chip) {
  color: rgba(255, 255, 255, 0.88) !important;
  border-color: rgba(255, 255, 255, 0.2) !important;
}

.mtt-customer-panel ::v-deep .v-pagination__item,
.mtt-customer-panel ::v-deep .v-pagination__navigation {
  color: rgba(255, 255, 255, 0.9) !important;
  background: rgba(255, 255, 255, 0.075) !important;
  box-shadow: none !important;
}

.mtt-customer-panel ::v-deep .v-pagination__item--active {
  color: #fff !important;
  background: var(--v-primary-base) !important;
}

.mtt-dialog-card {
  color: #f7f8f8 !important;
  border: 1px solid rgba(255, 255, 255, 0.12);
}

.mtt-dialog-card ::v-deep .v-card__text {
  color: rgba(255, 255, 255, 0.74) !important;
}

@media (max-width: 600px) {
  .mtt-workout-customers {
    padding-top: 4px;
  }

  .customer-header,
  .plan-card-content {
    align-items: flex-start;
    flex-direction: column;
  }

  .mtt-hero {
    align-items: flex-start;
    gap: 12px;
  }

  .mtt-hero-icon {
    width: 48px;
    height: 48px;
    flex-basis: 48px;
    border-radius: 15px;
  }

  .customer-identity,
  .new-plan-button {
    width: 100%;
  }

  .plan-actions {
    width: 100%;
    justify-content: flex-start;
  }

  .customer-list {
    max-height: 360px;
  }
}
</style>
